import GameApi from './game-api';
import {Transaction} from '@solana/web3.js';
import {PublicKey} from '@solana/web3.js';
import {getOrCreateAssociatedTokenAccount, createTransferCheckedInstruction} from 'spl-token2';

import SuperOgGameStates from "../constants/super-og-game-states";
import {Bounce, Fade, Flip, JackInTheBox, Reveal, Roll, Zoom} from "react-awesome-reveal";
import GameUtils from "./game-utils";
import SuperOgPanels from "../constants/super-og-panels";
import ChallengeImage from "../media/katana-utensil.png";

import {useState} from "react";
import {ReactFloatingBalloons} from "react-floating-balloons";
import {hostname} from "os";

const GAME_WALLET = new PublicKey('277xfhuuSyZC2aDbZQhRP322he4BCpDCMPctyXkumYXw');
const CHEF_COIN = new PublicKey('7BPCwgL97UMWcSuyUmDdNTzGnDvruyfGKTmUaSbLzohP');


const GameTypes = {
  Rarity: 0,
  OGist: 1,
  Traits: 2,
};

const GameClient = {
  gameTypes: function () {
    return GameTypes;
  },
  createBlankSuperOGChallengeGame: async function (walletId) {
    return await GameApi.post('/super-og-game', {walletId});
  },
  cancelGame: async function (game, walletPK) {
    return await GameApi.put(`/super-og-cancel/${walletPK}`, game);
  },
  getGameName: function (gameType) {
    switch (gameType) {
      case GameTypes.OGist:
        return 'The OGist';
      case GameTypes.Traits:
        return 'Trait v. Trait';
      case GameTypes.Rarity:
      default:
        return 'Rarity';
    }
  },
  getChefsInPlay: async function(walletPK) {
    const requestResult = await GameApi.get(`/super-og-chefs-in-play/${walletPK}`);
    if (requestResult) {
      return requestResult.data;
    } else {
      return [];
    }
  },
  payForChallenge: async function (connection, wallet, wager) {
    try {
      const fromTokenAccount = await getOrCreateAssociatedTokenAccount(connection, wallet, CHEF_COIN, wallet.publicKey);
      const toTokenAccount = await getOrCreateAssociatedTokenAccount(connection, wallet, CHEF_COIN, GAME_WALLET);
      let tx = new Transaction().add(
          createTransferCheckedInstruction(
              fromTokenAccount.address, // from (should be a token account)
              CHEF_COIN, // mint
              toTokenAccount.address, // to (should be a token account)
              wallet.publicKey,
              wager,
              0 // decimals
          )
      );
      const signature = await wallet.sendTransaction(tx, connection);
      await connection.confirmTransaction(signature, 'finalized');
      return signature;
    } catch (e) {
      console.log(e, 'Failed to pay for challenge.');
      return null;
    }

  },
  deploySuperOGChallenge: async function (championGame, connection, wallet, wager) {
    try {
      championGame.champTrxId = await GameClient.payForChallenge(connection, wallet, wager);
      if (championGame.champTrxId) {
        const result = await GameApi.put('/super-og-challenge', championGame);
        return result?.data;
      } else {
        return { ...championGame, state: SuperOgGameStates.champPaymentFailed };
      }
    } catch (err) {
      console.error({err}, 'Failed to deploySuperOGChallenge');
      return { ...championGame, state: SuperOgGameStates.error };
    }
  },
  acceptAndEvalSuperOGChallenge: async function (challengerGame, connection, wallet, wager) {
    try {
      const requestResult = await GameApi.get(`/super-og-accept-request/${challengerGame.id}`);
      if (requestResult?.data?.isOpen) {
        const acceptedResult = await GameApi.put(`/super-og-accepted/${challengerGame.id}`, challengerGame);
        if (acceptedResult?.data?.state === SuperOgGameStates.pendingChallengerPayment) {
          const trxId = await GameClient.payForChallenge(connection, wallet, wager);
          if (trxId) {
            const acceptedGame = {...acceptedResult.data.dataValues, ...challengerGame};
            acceptedGame.challengerTrxId = trxId;
            const result = await GameApi.put('/super-og-finalize', acceptedGame);
            return result.data;
          } else {
            // payment failed... move back to open state.
            await GameApi.get(`/super-og-re-open/${challengerGame.id}`);
            return {
              state: SuperOgGameStates.challengerPaymentFailed,
            }
          }
        }
      } else {
        return {
          state: SuperOgGameStates.started,
        }
      }
    } catch (err) {
      console.error({err}, 'Failed to acceptAndEvalSuperOGChallenge');
      throw err;
    }
  },
  getOpenSuperOGChallenges: async function (walletPK) {
    const result = await GameApi.get(`/super-og-open-challenges/${walletPK}`);
    return result?.data;
  },
  getMySuperOGGames: async function (walletPK) {
    const result = await GameApi.get(`/super-og-my-games/${walletPK}`);
    return result?.data;
  },
  fetchGameResult: async function (id) {
    const result = await GameApi.get(`/super-og-fetch-result/${id}`);
    return result?.data;
  },
  getAttr: function (nft, attr) {
    const attribute = nft.json.attributes?.find(a => a.trait_type === attr);
    return attribute?.value;
  },
  getStars: function (nft) {
    const rarity = GameClient.getAttr(nft, 'Rarity');
    return parseInt(rarity);
  },
  getStarsHtml: function (nft = null, starCount = null, animate = false) {
    if (!starCount) {
      const rarity = GameClient.getAttr(nft, 'Rarity');
      starCount = parseInt(rarity);
    }
    let stars = [];
    if (starCount === 0) { starCount = 1;} // never show zero stars.
    for (let i = 0; i < starCount; i++) {
      if (animate) {
        stars.push(<Fade><span className="fa-solid fa-star" /></Fade>);
      } else {
        stars.push(<i className="fa-solid fa-star"></i>);
      }
    }
    if (animate) {
      return <div className="flex flex-row">
        <Fade cascade damping={0.15}>
          {stars}
        </Fade>
      </div>;
    } else {
      return <span>{stars}</span>;
    }
  },
  getChefCard: function(chef) {
    return <div className="rounded-lg mx-auto flex flex-col p-2 bg-zinc-400">
      <div className="text-center bg-brand text-brand-text">
        {GameClient.getStarsHtml(null, chef.stars)}
      </div>
      <img src={chef.image} className="max-h-40 m-2 p-2" />
      <div className="text-center">{chef.name}</div>
    </div>;
  },
  formatMintDate: function(isoMintDateString) {
    const options = {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', timeZoneName: 'short',
    };
    const isoMintDate = new Date(isoMintDateString);
    const formatter = new Intl.DateTimeFormat('default', options);
    return formatter.format(isoMintDate);
  },
  getTurnDetails: function (game, turn, isExpandedRow) {
    switch (turn.gameType) {
      case GameTypes.Rarity:
        return <div className="flex flex-row mb-2 align-middle bg-zinc-900 rounded-lg">
          {
            isExpandedRow ? <div className="w-1/3 text-center">{GameClient.getGameTypeName(turn.gameType)}</div> : null
          }
          <div className={`${isExpandedRow ? 'w-1/3' : 'w-1/2'} text-center flex flex-col`}>
            <div>Rank: {game.champRarityRating}</div>
            {!turn.isChallengeSuccessful ? <div className="fa fa-check bg-green-500 text-bold p-1 rounded-lg text-xl w-full mb-1" /> : <div className="fa fa-x p-1 my-auto" />}
          </div>
          <div className={`${isExpandedRow ? 'w-1/3' : 'w-1/2'} text-center flex-flex-col`}>
            <div>Rank: {game.challengerRarityRating}</div>
            {turn.isChallengeSuccessful ? <div className="fa fa-check bg-green-500 text-bold p-1 rounded-lg  text-xl w-full mb-1" /> : <div className="fa fa-x p-1 my-auto" />}
          </div>
        </div>;
      case GameTypes.Traits:
        return <div className="flex flex-row mb-2 align-middle bg-zinc-600 rounded-lg">
          {
            isExpandedRow ? <div className="w-1/3 text-center">{GameClient.getGameTypeName(turn.gameType)}</div> : null
          }
          <div className={`${isExpandedRow ? 'w-1/3' : 'w-1/2'} text-center flex flex-col`}>
            <div className="text-xs">{game.champTrait}</div>
            <div>{game.champTraitRarity}</div>
            {!turn.isChallengeSuccessful ? <div className="fa fa-check bg-green-500 text-bold p-1 rounded-lg text-xl mb-1" /> : <div className="fa fa-x p-1 my-auto" />}
          </div>
          <div className={`${isExpandedRow ? 'w-1/3' : 'w-1/2'} text-center flex flex-col mb-1`}>
            <div className="text-xs">{game.challengerTrait}</div>
            <div>{game.challengerTraitRarity}</div>
            {turn.isChallengeSuccessful ? <div className="fa fa-check bg-green-500 text-bold p-1 rounded-lg text-xl mb-1" /> : <div className="fa fa-x p-1 my-auto" />}
          </div>
        </div>;
      case GameTypes.OGist:
        return <div className="flex flex-row mb-2 align-middle bg-zinc-700 rounded-lg">
          {
            isExpandedRow ? <div className="w-1/3 text-center">{GameClient.getGameTypeName(turn.gameType)}</div> : null
          }
          <div className={`${isExpandedRow ? 'w-1/3' : 'w-1/2'} text-center`}>
            <div className="text-xs">{GameClient.formatMintDate(game.champOGDate)}</div>
            {!turn.isChallengeSuccessful ? <div className="fa fa-check bg-green-500 text-bold p-1 rounded-lg text-xl w-full mb-1" /> : <div className="fa fa-x p-1 my-auto" />}
          </div>
          <div className={`${isExpandedRow ? 'w-1/3' : 'w-1/2'} text-center`}>
            <div className="text-xs">{GameClient.formatMintDate(game.challengerOGDate)}</div>
            {turn.isChallengeSuccessful ? <div className="fa fa-check bg-green-500 text-bold p-1 rounded-lg text-xl w-full mb-1" /> : <div className="fa fa-x p-1 my-auto" />}
          </div>
        </div>;
    }
  },
  getTurnPith: function(game, turn, round, isPlayerChallenger) {
    return <div>
    <div className="flex flex-row space-x-1 mt-2">
        <div className="w-1/2">
          <div className="text-center">{isPlayerChallenger ? 'Opponent' : 'You'}</div>
          {GameClient.getChefCard(isPlayerChallenger ? turn.challenger : turn.champion)}
        </div>
        <div className="w-1/2">
          <div className="text-center">{isPlayerChallenger ? 'You' : 'Opponent'}</div>
          {GameClient.getChefCard(isPlayerChallenger ? turn.champion : turn.challenger)}
        </div>
      </div>
      <div>
        {GameClient.getTurnDetails(game, turn)}
      </div>
      <Flip delay={2000}>
        <div className="text-4xl text-center">
          {(turn.isChallengeSuccessful && isPlayerChallenger) || (!turn.isChallengeSuccessful && !isPlayerChallenger) ? 'You Win' : 'You Lose'}
        </div>
      </Flip>
    </div>;
  },
  getTurnFrame: function(game, index, isPlayerChallenger) {
    const turn = game.turns[index];
    const round = index + 1;

    switch(index) {
      case 2:
        return {
          duration: 5000,
          content:  <Reveal cascade>
            <Fade top>
              <div className=" my-auto">
                <Zoom>
                  <div className="p-2 text-center">Round<span className="text-4xl font-extrabold">&nbsp;{round}</span></div>
                </Zoom>
                <div className="text-4xl bg-zinc-400 p-4 bg-brand text-brand-text rounded-lg text-center">{GameClient.getGameTypeName(turn.gameType)}</div>
              </div>
            </Fade>
            {GameClient.getTurnPith(game, turn, round, isPlayerChallenger)}
          </Reveal>
        };
      case 0:
        return {
          duration: 4000,
          content:  <JackInTheBox cascade>
            <Bounce top>
              <div className=" my-auto">
                <Zoom>
                  <div className="p-2 text-center">Round<span className="text-4xl font-extrabold">&nbsp;{round}</span></div>
                </Zoom>
                <div className="text-4xl bg-zinc-400 p-4 bg-brand text-brand-text rounded-lg text-center">{GameClient.getGameTypeName(turn.gameType)}</div>
              </div>
            </Bounce>
            {GameClient.getTurnPith(game, turn, round, isPlayerChallenger)}
          </JackInTheBox>
        };
      case 1:
      default:
        return {
          duration: 4000,
          content:  <Fade cascade>
            <Bounce top>
              <div className=" my-auto">
                <Zoom>
                  <div className="p-2 text-center">Round<span className="text-4xl font-extrabold">&nbsp;{round}</span></div>
                </Zoom>
                <div className="text-4xl bg-zinc-400 p-4 bg-brand text-brand-text rounded-lg text-center">{GameClient.getGameTypeName(turn.gameType)}</div>
              </div>
            </Bounce>
            {GameClient.getTurnPith(game, turn, round, isPlayerChallenger)}
          </Fade>
        };
    }
  },
  getChallengeLink: function(game) {
    if (game && game?.state === SuperOgGameStates.open) {
        return `${window.location.protocol}//${window.location.host}/#/super-og/3/0/${game.id}/`;
    } else if (game && game.state) {
      return `${window.location.protocol}//${window.location.host}/#/super-og/5/0/${game.id}/`;
    } else {
      return '';
    }
  },
  getGameTypeName: function(gameType) {
    switch (gameType) {
      case 0:
        return 'One-of-a-kind';
      case 1:
        return 'The OGist';
      case 2:
      default:
        return 'Trait vs. Trait';
    }
  },
  getShowReelFrames: async function(game, playerPK, onChangePanel, frameOverride = -1) {
    const isPlayerChallenger = playerPK === game?.challengerWalletPK;
    const frames = [
      {
        duration: 5000,
        content:  <div>
          <Zoom>
            <div>
              <div className="text-2xl text-center">{GameUtils.getGameChallengeName(game.handle)}</div>
              <div className="w-48 mx-auto">
                <img className="mx-auto" src={ChallengeImage} />
              </div>
            </div>
          </Zoom>
          <Fade right delay={1000}>
            <div className="text-center text-2xl rounded-lg bg-brand text-brand-text p-4">{game.handle}</div>
            <Flip delay={2000}>
              <div className="text-2xl p-4 text-center">
                {GameUtils.getGameQuip(game.handle)}
              </div>
            </Flip>
          </Fade>
        </div>
      }, // intro
      {
        duration: 6000,
        content:  <div>
          <Reveal left><div className="text-center p-2">Meet Your Opponent</div></Reveal>
          <div className="h-60">
            <Reveal cascade damping={2} className="">
              <JackInTheBox >
                <Fade reverse delay={2000} className="absolute left-1/4">{GameClient.getChefCard(!isPlayerChallenger ? game.turns[0].challenger : game.turns[0].champion)}</Fade>
              </JackInTheBox>
              <Zoom className="absolute left-1/4">
                <Fade reverse delay={5000}>
                  {GameClient.getChefCard(!isPlayerChallenger ? game.turns[1].challenger : game.turns[1].champion)}
                </Fade>
              </Zoom>
              <Flip className="absolute left-1/4">
                {GameClient.getChefCard(!isPlayerChallenger ? game.turns[2].challenger : game.turns[2].champion)}
              </Flip>
            </Reveal>
          </div>
          <div className="mx-auto p-2 bg-zinc-400 rounded-lg m-2">
            <div className="flex">
              <span className="w-8 text-right mr-2">You:</span>&nbsp;
              {GameClient.getStarsHtml(null, game.turns[0].champion.stars + game.turns[1].champion.stars + game.turns[2].champion.stars, true)}
            </div>
            <div className="flex">
              <span className="w-8 text-right mr-2">Opp:</span>&nbsp;
              {GameClient.getStarsHtml(null, game.turns[0].challenger.stars + game.turns[1].challenger.stars + game.turns[2].challenger.stars, true)}
            </div>
          </div>
          <Fade delay={4}>
            <div className="rounded-lg bg-brand text-brand-text text-center p-4 text-2xl">{GameUtils.getOpponentComment(game.turns[0].champion.stars + game.turns[1].champion.stars + game.turns[2].champion.stars, game.turns[0].challenger.stars + game.turns[1].challenger.stars + game.turns[2].challenger.stars)}</div>
          </Fade>
        </div>
      }, // meet your opponent
      {
        duration: 4000,
        content:  <div>
          <Flip delay={2000}>
            <div className="text-4xl text-center bg-brand text-brand-text p-2 rounded-lg mb-2">
              The Draw
            </div>
          </Flip>
          <table className="table-auto mx-auto">
            <tbody className="">
              <Fade cascade>
              {
                game.turns.map(turn => {
                  switch (turn.gameType) {
                    case GameTypes.Rarity:
                      return <Fade><tr className="p-2">
                        <td><div className="h-4"></div><span className="text-2xl rounded-full bg-brand p-4 fa fa-medal my-auto h-100" /></td>
                        <td><div className="text-xl text-center p-1">One-of-a-kind</div></td>
                        <td><div className="p-2 bg-zinc-400 rounded-lg text-sm font-bold text-center">rarest OG Chef</div></td>
                      </tr></Fade>;
                    case GameTypes.OGist:
                      return <Fade><tr className="p-2">
                        <td><div className="text-2xl rounded-full bg-brand p-4 fa fa-calendar-day my-auto  h-100" /></td>
                        <td><div className="text-xl text-center p-7">The OGist</div></td>
                        <td><div className="p-2 bg-zinc-400 rounded-lg text-sm font-bold text-center">first mint date</div></td>
                      </tr></Fade>;
                    case GameTypes.Traits:
                      return <Fade><tr className="p-2">
                        <td><div className="text-3xl rounded-full bg-brand p-4 fa fa-person my-auto h-100" /></td>
                        <td><div className="text-xl text-center p-3">Trait vs. Trait</div></td>
                        <td><div className="p-1 bg-zinc-400 rounded-lg font-bold text-xs text-center">rarest random trait</div></td>
                      </tr></Fade>;
                  }
                })
              }
              </Fade>
            </tbody>
          </table>
        </div>
      }, // the draw
      GameClient.getTurnFrame(game,0, isPlayerChallenger), // Round 1
      GameClient.getTurnFrame(game,1, isPlayerChallenger), // Round 2
      GameClient.getTurnFrame(game,2, isPlayerChallenger), // Round 3
      {
        duration: 5000,
        content:  <div>
          {game.payoutWalletPK === playerPK ?
              <ReactFloatingBalloons
                  loop={false}
                  hangOnTop={false}
                  count={1}
                  msgText="Click me!"
                  colors={["red"]}
                  popVolumeLevel={0.1}
              />
              :
              null}
          <Flip >
            <div className="text-2xl text-center">
              Game Summary
            </div>
          </Flip>
          <Reveal cascade>
            <div className="container flex flex-col">
              <div className="flex flex-row mb-2 align-middle bg-brand">
                <div className="w-1/3 text-center">Game</div>
                <div className={`w-1/3 text-center${game.payoutWalletPK === playerPK ? ' bg-green-500 font-bold' : ''}`}>
                  {isPlayerChallenger ? 'Opponent' : 'You'}
                </div>
                <div className={`w-1/3 text-center${game.payoutWalletPK !== playerPK ? ' bg-green-500 font-bold' : ''}`}>
                  {!isPlayerChallenger ? 'Opponent' : 'You'}
                </div>
              </div>
                <Fade>
                  {
                    game.turns.map(turn => {
                      return GameClient.getTurnDetails(game, turn, true);
                    })
                  }
                </Fade>
              <div className="flex flex-row mb-2 align-middle my-auto">
                <div className="w-1/3 text-center">{game.payoutWalletPK === playerPK ? 'You Win' : 'You Lose'}</div>
                <div className={`w-1/3 text-center`}>
                  <div className="text-xl">{game.champPoints}</div>
                </div>
                <div className={`w-1/3 text-center`}>
                  <div className="text-xl">{game.challengerPoints}</div>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="relative">
            <Zoom delay={2000}>
              <Flip reverse delay={3000}>
                <Fade reverse delay={4000}>
                  <div className="text-4xl text-center">{game.payoutWalletPK === playerPK ? 'You win!' : 'You lose'}</div>
                </Fade>
              </Flip>
            </Zoom>
            <Fade delay={4500}>
                <div className="absolute top-0">{GameUtils.getTaunt(game.champPoints, game.challengerPoints, isPlayerChallenger)}</div>
            </Fade>
          </div>
          <div className="h-8" />
          <div className="flex flex-row space-x-1 justify-between">
            <button className="fa fa-refresh rounded-lg bg-brand p-4" onClick={async () => {
              onChangePanel(SuperOgPanels.ShowReelReplay, {game: game});
            }} />
            <button className="rounded-lg bg-brand text-sm text-brand-text p-2 text-center" onClick={()=> {onChangePanel(SuperOgPanels.Lobby)}}>Go to Lobby</button>
          </div>
        </div>
      }, // Game Summary
    ];

    if (frameOverride > -1) {
      return frames.filter((frame, index) => index === frameOverride); // filter all the frames but the overridden one for easy debugging.
    } else {
      return frames;
    }
  },
};
export default GameClient;